// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Share Menu Component
// ═══════════════════════════════════════════════════════════════════════════
// Enhanced share with native share + community share options
// ═══════════════════════════════════════════════════════════════════════════

import { icons } from './icons.js';
import { isAuthenticated, getCurrentUser } from '../services/auth.js';
import { showAuthPrompt } from './AuthPrompt.js';
import { shareVersePublicly } from '../services/firestore.js';
import { logShare } from '../services/analytics.js';

let currentOverlay = null;
let currentVerse = null;

// ─────────────────────────────────────────────────────────────────────────
// SHOW SHARE MENU
// ─────────────────────────────────────────────────────────────────────────

export function showShareMenu(verse) {
  if (currentOverlay) hideShareMenu();

  currentVerse = verse;

  // Native share is only meaningful where the Web Share API exists (mostly
  // mobile). On unsupported browsers we hide the option instead of showing a
  // dead "Coming Soon" button (#43).
  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const overlay = document.createElement('div');
  overlay.className = 'share-menu-overlay';
  overlay.id = 'share-menu-overlay';

  overlay.innerHTML = `
    <div class="share-menu-panel">
      <div class="share-menu-handle"></div>

      <h3 class="share-menu-title">Share Verse</h3>

      <div class="share-menu-options">
        ${hasNativeShare ? `
        <button class="share-menu-option" id="share-native">
          <div class="share-menu-icon">
            ${icons.share}
          </div>
          <div class="share-menu-option-text">
            <span class="share-menu-option-title">Share to Apps</span>
            <span class="share-menu-option-desc">Send to a friend or another app</span>
          </div>
        </button>
        ` : ''}

        <button class="share-menu-option" id="share-community">
          <div class="share-menu-icon community-icon">
            ${icons.users}
          </div>
          <div class="share-menu-option-text">
            <span class="share-menu-option-title">Share with Community</span>
            <span class="share-menu-option-desc">Let others discover this verse</span>
          </div>
        </button>
        
        <button class="share-menu-option" id="share-copy">
          <div class="share-menu-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </div>
          <div class="share-menu-option-text">
            <span class="share-menu-option-title">Copy Text</span>
            <span class="share-menu-option-desc">Prepared to share</span>
          </div>
        </button>
      </div>
      
      <button class="share-menu-cancel" id="share-cancel">Cancel</button>
    </div>
  `;

  document.body.appendChild(overlay);
  currentOverlay = overlay;

  // Animate in
  requestAnimationFrame(() => {
    overlay.classList.add('active');
  });

  // Setup events
  setupShareMenuEvents(overlay);
}

export function hideShareMenu() {
  if (!currentOverlay) return;

  currentOverlay.classList.remove('active');
  setTimeout(() => {
    currentOverlay?.remove();
    currentOverlay = null;
    currentVerse = null;
  }, 300);
}

// ─────────────────────────────────────────────────────────────────────────
// EVENT HANDLERS
// ─────────────────────────────────────────────────────────────────────────

function setupShareMenuEvents(overlay) {
  // Backdrop click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) hideShareMenu();
  });

  // Cancel button
  overlay.querySelector('#share-cancel')?.addEventListener('click', hideShareMenu);

  // Native share
  overlay.querySelector('#share-native')?.addEventListener('click', handleNativeShare);

  // Community share
  overlay.querySelector('#share-community')?.addEventListener('click', handleCommunityShare);

  // Copy
  overlay.querySelector('#share-copy')?.addEventListener('click', handleCopyShare);
}

async function handleNativeShare() {
  if (!currentVerse) return;

  const shareData = {
    title: currentVerse.reference,
    text: `"${currentVerse.text}"\n\n— ${currentVerse.reference}`,
  };

  // Check if Web Share API is available
  if (navigator.share) {
    try {
      // Try sharing directly - many mobile browsers support text sharing
      // even when canShare returns false or doesn't exist
      await navigator.share(shareData);
      logShare(currentVerse.reference, 'native', true);
      showToast('Shared successfully');
      hideShareMenu();
      return;
    } catch (e) {
      if (e.name === 'AbortError') {
        // User cancelled the share dialog
        hideShareMenu();
        return;
      }
      // Other errors - fall through to clipboard fallback
      console.warn('Native share failed, falling back to clipboard:', e);
    }
  }

  // Fallback to clipboard for browsers without share support
  await handleCopyShare();
}

async function handleCommunityShare() {
  if (!currentVerse) return;

  // Check auth
  if (!isAuthenticated()) {
    hideShareMenu();
    showAuthPrompt(async () => {
      // After auth, share to community
      await shareToCommunity();
    }, 'share');
    return;
  }

  await shareToCommunity();
}

async function shareToCommunity() {
  if (!currentVerse) return;

  const btn = currentOverlay?.querySelector('#share-community');
  if (btn) {
    btn.disabled = true;
    btn.querySelector('.share-menu-option-desc').textContent = 'Sharing...';
  }

  try {
    const result = await shareVersePublicly(currentVerse, false);

    if (result.success) {
      logShare(currentVerse.reference, 'community', true);
      showToast('Shared with the community!');
      hideShareMenu();
    } else {
      showToast(result.error || 'Failed to share. Please try again.');
      if (btn) {
        btn.disabled = false;
        btn.querySelector('.share-menu-option-desc').textContent = 'Let others discover this verse';
      }
    }
  } catch (e) {
    console.error('Community share error:', e);
    showToast('Failed to share. Please try again.');
    if (btn) {
      btn.disabled = false;
      btn.querySelector('.share-menu-option-desc').textContent = 'Let others discover this verse';
    }
  }
}

async function handleCopyShare() {
  if (!currentVerse) return;

  const text = `"${currentVerse.text}"\n\n— ${currentVerse.reference}`;

  // Try modern clipboard API first
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      logShare(currentVerse.reference, 'copy', true);
      showToast('Prepared to share');
      hideShareMenu();
      return;
    } catch (e) {
      console.warn('Clipboard API failed, trying fallback:', e);
    }
  }

  // Fallback: use deprecated but reliable execCommand
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    const success = document.execCommand('copy');
    document.body.removeChild(textarea);

    if (success) {
      logShare(currentVerse.reference, 'copy', true);
      showToast('Prepared to share');
      hideShareMenu();
    } else {
      showToast('Could not copy. Long-press to select and copy.');
    }
  } catch (e) {
    console.error('Copy fallback failed:', e);
    showToast('Could not copy. Long-press to select and copy.');
  }
}

function showToast(message) {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(-50%) translateY(20px)';
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

// ─────────────────────────────────────────────────────────────────────────
// EXPORT
// ─────────────────────────────────────────────────────────────────────────

export default { showShareMenu, hideShareMenu };
